// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  DsKpiCard,
  GradePager,
  GradeCanvas,
  DsPageHeader,
  DsEmptyState,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// Parcelamento → Meus Parcelamentos (Localizar). Backend:
// GET /portal-api/parcelamento (migracao.view_parcelamentos agrupado por acordo,
// filtrado por cnpj_cnt = login). Ver SPEC-PORTAL-SN-TOMADOR-PF.md §7.
//
// Canvas (portal-parcelamento.dc.html): cabeçalho com CTA, 4 KPIs em grid 1fr×4
// com border-left de 3px, e corpo em duas colunas — grade (flex:1) + coluna fixa
// de 300px com o resumo e o botão primário de largura total.
// O rodapé de paginação permanece por decisão do dono do produto (o artboard não
// o desenha), agora no formato `.pager` do canvas.
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

interface Parcelamento {
  numero: number;
  receita: string | null;
  exercicio: string | null;
  parcelas: number;
  parcelas_com_boleto: number;
  valor_total: number;
  ultimo_vencimento: string | null;
}

interface ParcelamentoResponse {
  parcelamentos: Parcelamento[];
}

const TAMANHO_PAGINA = 10;

function hoje(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Acordo cujo último vencimento já passou é tratado como encerrado. */
function encerrado(parc: Parcelamento): boolean {
  if (!parc.ultimo_vencimento) return false;
  const t = new Date(parc.ultimo_vencimento).getTime();
  return Number.isFinite(t) && t < hoje();
}

/** `.kv` do canvas: rótulo à esquerda, valor tabular à direita, régua tracejada. */
function LinhaKv({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        fontSize: 12,
        borderBottom: '1px dashed',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {rotulo}
      </Box>
      <DsNum sx={{ fontWeight: 700 }}>{valor}</DsNum>
    </Box>
  );
}

export default function ParcelamentoListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<ParcelamentoResponse>('/portal-api/parcelamento', fetcher);
  const [pagina, setPagina] = useState(0);

  const parcelamentos = data?.parcelamentos ?? [];
  const vazio = !isLoading && parcelamentos.length === 0;

  const ativos = parcelamentos.filter((p) => !encerrado(p));
  const totalAberto = ativos.reduce((acc, parc) => acc + (parc.valor_total ?? 0), 0);
  const totalParcelas = parcelamentos.reduce((acc, parc) => acc + (parc.parcelas ?? 0), 0);
  const totalBoletos = parcelamentos.reduce(
    (acc, parc) => acc + (parc.parcelas_com_boleto ?? 0),
    0
  );
  const ultimoVencimento = parcelamentos
    .map((p) => p.ultimo_vencimento)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1);

  const paginaAtual = parcelamentos.slice(pagina * TAMANHO_PAGINA, (pagina + 1) * TAMANHO_PAGINA);

  const columns: GridColDef<Parcelamento>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      width: 130,
      renderCell: (params) => <DsNum mono>{params.row.numero}</DsNum>,
    },
    {
      field: 'parcelas',
      headerName: 'Parcelas',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) =>
        [
          `${row.parcelas} parcela(s)`,
          `${row.parcelas_com_boleto} com boleto`,
          row.receita,
          row.exercicio,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    {
      field: 'valor_total',
      headerName: 'Valor total (R$)',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor_total)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 120,
      sortable: false,
      renderCell: (params) =>
        encerrado(params.row) ? (
          <DsStatusChip>Encerrado</DsStatusChip>
        ) : (
          <DsStatusChip tone="success">Ativo</DsStatusChip>
        ),
    },
    {
      field: 'acoes',
      headerName: '',
      width: 150,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (params) => (
        <DsButton
          tone="neutral"
          sx={{ padding: '6px 12px', fontSize: 12 }}
          onClick={() => navigate(`/parcelamento/${params.row.numero}`)}
        >
          Ver parcelas
        </DsButton>
      ),
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Parcelamento"
        subtitle="Regras ativas, simulação e acompanhamento de parcelas."
        action={
          <DsButton icon="mais" size="tall" onClick={() => navigate('/parcelamento/novo')}>
            Novo parcelamento
          </DsButton>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Parcelamentos ativos"
          total={<DsNum>{ativos.length}</DsNum>}
          sub={`${formatCurrency(totalAberto)} em aberto`}
          conceito="parcelamentos"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Parcelas contratadas"
          total={<DsNum>{totalParcelas}</DsNum>}
          sub={`${totalBoletos} com boleto emitido`}
          conceito="parcelas"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Em atraso"
          total={<DsNum>—</DsNum>}
          sub="situação por parcela vive no acordo"
          conceito="vencido"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Encerrados"
          total={<DsNum>{parcelamentos.length - ativos.length}</DsNum>}
          sub={ultimoVencimento ? `último vencimento ${formatDateBr(ultimoVencimento)}` : '—'}
          conceito="encerrado"
          borderLeft
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <GradeCanvas
            pager={
              <GradePager
                de={parcelamentos.length === 0 ? 0 : pagina * TAMANHO_PAGINA + 1}
                ate={Math.min((pagina + 1) * TAMANHO_PAGINA, parcelamentos.length)}
                total={parcelamentos.length}
                onPrev={() => setPagina((p) => Math.max(0, p - 1))}
                onNext={() => setPagina((p) => p + 1)}
                prevDisabled={pagina === 0}
                nextDisabled={(pagina + 1) * TAMANHO_PAGINA >= parcelamentos.length}
              />
            }
          >
            {vazio ? (
              <DsEmptyState
                icon="calendario"
                title="Nenhum parcelamento encontrado"
                description="Você ainda não tem acordo de parcelamento. Simule um novo acordo para dividir os débitos em aberto."
                action={
                  <DsButton icon="mais" size="tall" onClick={() => navigate('/parcelamento/novo')}>
                    Novo parcelamento
                  </DsButton>
                }
              />
            ) : (
              <TxDataGrid<Parcelamento>
                rows={paginaAtual}
                columns={columns}
                loading={isLoading}
                emptyLabel="Nenhum parcelamento encontrado"
                getRowId={(row) => row.numero}
                initialPageSize={TAMANHO_PAGINA}
                hideFooter
                {...PROPS_GRADE}
                sx={SX_GRADE}
              />
            )}
          </GradeCanvas>
        </Box>

        {/*
          Coluna fixa de 300px do artboard. Lá ela mostra a SIMULAÇÃO de um novo
          acordo (regra, nº de parcelas, total com desconto) — números que só o
          wizard de `/parcelamento/novo` calcula. Aqui ela mantém a geometria e o
          botão primário do canvas com o que a lista realmente sabe.
        */}
        <Card
          sx={{
            flex: { xs: '1 1 auto', md: '0 0 300px' },
            width: '100%',
            padding: '18px 20px',
          }}
        >
          <Box component="h2" sx={{ fontSize: 13, fontWeight: 800, m: '0 0 6px' }}>
            Resumo dos acordos
          </Box>

          <LinhaKv rotulo="Acordos ativos" valor={ativos.length} />
          <LinhaKv rotulo="Encerrados" valor={parcelamentos.length - ativos.length} />
          <LinhaKv rotulo="Parcelas contratadas" valor={totalParcelas} />
          <LinhaKv rotulo="Parcelas com boleto" valor={totalBoletos} />
          <LinhaKv rotulo="Total em aberto" valor={formatCurrency(totalAberto)} />
          <LinhaKv
            rotulo="Último vencimento"
            valor={ultimoVencimento ? formatDateBr(ultimoVencimento) : '—'}
          />

          <Box sx={{ mt: '12px' }}>
            <DsButton
              size="tall"
              onClick={() => navigate('/parcelamento/novo')}
              sx={{ width: '100%', justifyContent: 'center' }}
            >
              Cadastrar Parcelamento
            </DsButton>
          </Box>
        </Card>
      </Box>
    </DashboardContent>
  );
}
